/* eslint-disable guard-for-in */

import { AfterViewInit, Component, Inject, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { icon, latLngBounds, map, Map, marker, Marker, tileLayer } from 'leaflet';
import { catchError, throwError } from 'rxjs';
import { DialogsService } from '../dialogs.service';
import { FuseCardComponent } from '../../../@fuse/components/card/card.component';
import { LocationChangeRequestData } from '../../core/http/reply.model';
import { DTypeService } from '../../core/dtype/dtype.service';
import { FileService } from '../../core/file/file.service';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { MasterService } from '../../core/master/master.service';
import { LocalizeService } from '../../core/pipes/localize.service';

@Component({
    selector: 'app-location-change-dialog',
    templateUrl: './location-change-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
            .location-change-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ],
    imports: [TranslocoModule, MatIconModule, FuseCardComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None
})
export class LocationChangeDialogComponent implements AfterViewInit {

    requestData: LocationChangeRequestData;
    imageUrl: string;
    mapId = 'locationsMap';

    itemMarker: Marker;
    requestMarker: Marker;
    private map: Map;

    private itemIcon = icon({
        iconUrl: '/leaflet/images/marker-icon.png',
        shadowUrl: '/leaflet/images/marker-shadow.png',
        iconRetinaUrl: '/leaflet/images/marker-icon-2x.png',
        iconSize: [25, 41], // size of the icon
        shadowSize: [32, 32], // size of the shadow
        iconAnchor: [10, 40], // point of the icon which will correspond to marker's location
        shadowAnchor: [2, 30],  // the same for the shadow
    });

    private requestIcon = icon({
        iconUrl: 'assets/images/position-markers/marker-icon-red.png',
        shadowUrl: '/leaflet/images/marker-shadow.png',
        iconRetinaUrl: 'assets/images/position-markers/marker-icon-2x-red',
        iconSize: [25, 41], // size of the icon
        shadowSize: [32, 32], // size of the shadow
        iconAnchor: [10, 40], // point of the icon which will correspond to marker's location
        shadowAnchor: [2, 30],  // the same for the shadow
    });

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            item: any;
        },
        private dialogsService: DialogsService,
        public dialogRef: MatDialogRef<LocationChangeDialogComponent>,
        private dTypeService: DTypeService,
        private fileService: FileService,
        private _fuseConfirmationService: FuseConfirmationService,
        private masterService: MasterService,
        private transloco: TranslocoService,
        private localize: LocalizeService
    ) { }

    ngAfterViewInit() {
        if (this.data.item.requestData) {
            this.requestData = this.data.item.requestData;
            this.imageUrl = this.fileService.getDownloadImageUrl(this.requestData.photo, 480);
            this.initMap();
        } else {
            this.masterService.retrieveLocationChangeRequest({
                includeMasterData: false,
                includeRequestData: true,
                oids: [this.data.item._oid]
            }).pipe(
                catchError((error) => {
                    console.error(error);
                    this.dialogsService.openErrorDialog();
                    return throwError(() => error);
                }),
            ).subscribe(reply => {
                if (reply.requestData?.length > 0) {
                    this.requestData = reply.requestData[0];
                    this.imageUrl = this.fileService.getDownloadImageUrl(this.requestData.photo, 480);
                    this.initMap();
                }
            });
        }
    }

    approve() {
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('approveChangeLocationConfirm'));
        confirmDialog.afterClosed().subscribe((confirm) => {
            if (confirm === 'confirmed') {
                this.closeDialog('approve');
            }
        });
    }

    reject() {
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('rejectChangeLocationConfirm'));
        confirmDialog.afterClosed().subscribe((confirm) => {
            if (confirm === 'confirmed') {
                this.closeDialog('reject');
            }
        });
    }

    async closeDialog(result?: 'approve' | 'reject') {
        this.dialogRef.close(result);
    }

    centerMapTo(marker: Marker) {
        this.map.setView({ lat: marker.getLatLng().lat, lng: marker.getLatLng().lng }, 18, { animate: true, duration: 2 });
    }

    private openConfirmDialog(message: string) {
        return this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });
    }

    private initMap() {
        //Create new map centered on Madrid
        this.map = map(this.mapId, {
            center: [40.416877, -3.703526],
            zoom: 16,
            doubleClickZoom: false,
        });

        if (this.data.item.address) {
            this.paintMarker(this.data.item.address.latitude, this.data.item.address.longitude, 'item');
        }
        if (this.requestData.address) {
            this.paintMarker(this.requestData.address.latitude, this.requestData.address.longitude, 'request');
        }
        this.centerMap();

        //Set source of tiles
        const tiles = tileLayer(
            'https://maps.krama.es/hot/{z}/{x}/{y}.png',
            {
                maxZoom: 18,
                minZoom: 3,
                attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            });

        // Add tile to map
        tiles.addTo(this.map);
    }

    private centerMap() {
        const markerBounds = latLngBounds([]);
        markerBounds.extend(this.itemMarker.getLatLng());
        markerBounds.extend(this.requestMarker.getLatLng());
        this.map.fitBounds(markerBounds);
    }

    private paintMarker(lat: number, lng: number, type: 'item' | 'request') {
        let mark = type === 'item' ? this.itemMarker : this.requestMarker;
        const icon = type === 'item' ? this.itemIcon : this.requestIcon;
        if (mark) {
            this.map.removeLayer(mark);
        }
        mark = marker([lat, lng], { icon: icon });
        mark.addEventListener('click', () => this.centerMapTo(mark));
        type === 'item' ? this.itemMarker = mark : this.requestMarker = mark;
        this.map.addLayer(mark);
    }

    private closeConfirmDialog(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.transloco.translate('unsavedChanges'),
            message: message ? message : this.transloco.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.transloco.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }

}
